/**
 * 各 Reward 任务在 Web 端的完成 / 领取逻辑。
 */
import { getClientEnv, Runtime } from '@/assets/js/clientEnv'
import { trackAddHomescreenPwaOpen } from '@/assets/js/addHomescreenAnalytics'
import { ENABLE_REWARD_TASKS } from '@/assets/js/featureFlags'
import {
  getRewardTasks,
  executeTask,
  claimRewardTask,
  TASK_STATUS
} from '@/service/rewardService'

export const REWARD_TASK_ID = {
  BROWSER: 'task_browser',
  HOMESCREEN: 'task_homescreen',
  SHARE: 'task_share',
  FB_PAGE: 'task_fb_page',
  WATCH_20MIN: 'task_watch_20min',
  LOGIN: 'task_login',
  ADD_HOMESCREEN: 'task_add_homescreen',
  TOP_UP: 'task_top_up',
  FIRST_FAVORITE: 'task_first_favorite',
  FEEDBACK: 'task_feedback'
}

const PWA_ONLY_REWARD_TASK_IDS = new Set([
  REWARD_TASK_ID.HOMESCREEN,
  REWARD_TASK_ID.ADD_HOMESCREEN
])

/**
 * 计算播放页福利引导可获得的观影币。未完成和可领取均计入，仅排除已领取。
 */
export function getPlayRewardAmounts (tasks, runtime) {
  const unclaimedTasks = (Array.isArray(tasks) ? tasks : [])
    .filter(task => task?.status !== TASK_STATUS.CLAIMED)

  const sum = list => list.reduce(
    (total, task) => total + Math.max(0, Number(task?.amount) || 0),
    0
  )

  if (runtime === Runtime.WEBVIEW) {
    const n = sum(unclaimedTasks)
    return { n, current: 0, pwaExtra: 0, total: n }
  }

  if (runtime === Runtime.PWA) {
    const n = sum(
      unclaimedTasks.filter(task => task?.task_id !== REWARD_TASK_ID.BROWSER)
    )
    return { n, current: 0, pwaExtra: 0, total: n }
  }

  const current = sum(
    unclaimedTasks.filter(task => !PWA_ONLY_REWARD_TASK_IDS.has(task?.task_id))
  )
  const pwaExtra = sum(
    unclaimedTasks.filter(task => PWA_ONLY_REWARD_TASK_IDS.has(task?.task_id))
  )
  return { n: 0, current, pwaExtra, total: current + pwaExtra }
}

export const TOP_UP_TASK_COMPLETED_STORAGE_KEY = 'appleshort_top_up_task_completed'

/** AppleShort Facebook 公共主页 */
export const FACEBOOK_PAGE_URL =
  'https://www.facebook.com/AppleShortTv'

export function openFacebookPage () {
  if (typeof window === 'undefined') return true

  try {
    // 不使用 noopener 特性字符串，尽量保留 window.open 返回值以识别弹窗是否被拦截。
    const popup = window.open(FACEBOOK_PAGE_URL, '_blank')
    if (!popup) return false

    try {
      popup.opener = null
    } catch {
      // 跨浏览器兜底：无法设置 opener 时，不影响“已发起打开”的任务判断。
    }
    return true
  } catch {
    return false
  }
}

/** 是否为 WebView 内打开 */
export function isWebViewRuntime () {
  return getClientEnv().runtime === Runtime.WEBVIEW
}

/** 是否为系统浏览器打开（非 WebView、非 PWA） */
export function isBrowserRuntime () {
  return getClientEnv().runtime === Runtime.BROWSER
}

/** 是否为 PWA / 从桌面打开 */
export function isPwaRuntime () {
  return getClientEnv().runtime === Runtime.PWA
}

function findTaskById (tasks, taskId) {
  return (tasks || []).find((item) => item?.task_id === taskId) || null
}

/** 仅 status=0 时 execute；刚变为可领取才返回 task（供庆祝弹窗使用） */
async function tryExecuteTaskIfUnfinished (uid, tasks, taskId, position) {
  const task = findTaskById(tasks, taskId)
  if (!task || task.status !== TASK_STATUS.UNFINISHED) {
    return null
  }

  const updated = await executeTask(uid, taskId, 1, position)
  if (!updated || updated.status !== TASK_STATUS.CLAIMABLE) {
    return null
  }

  return updated
}

export function isCoinTopUpProduct (product) {
  const type = String(product?.type || '').toLowerCase()
  return type === 'coin' || type === 'coins'
}

export function persistPendingTopUpTask (task) {
  if (!task?.task_id || typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(TOP_UP_TASK_COMPLETED_STORAGE_KEY, JSON.stringify(task))
}

export function readAndClearPendingTopUpTask () {
  if (typeof sessionStorage === 'undefined') return null
  const raw = sessionStorage.getItem(TOP_UP_TASK_COMPLETED_STORAGE_KEY)
  sessionStorage.removeItem(TOP_UP_TASK_COMPLETED_STORAGE_KEY)
  if (!raw) return null

  try {
    const task = JSON.parse(raw)
    return task?.task_id ? task : null
  } catch {
    return null
  }
}

/** 金币首充成功后尝试完成 task_top_up（仅 status=0 时执行） */
export async function tryAutoCompleteTopUpTask (uid, product, position) {
  if (!uid || !isCoinTopUpProduct(product)) return null

  try {
    const { tasks } = await getRewardTasks(uid)
    return tryExecuteTaskIfUnfinished(uid, tasks, REWARD_TASK_ID.TOP_UP, position)
  } catch {
    return null
  }
}

/**
 * 金币支付成功后执行首充任务完成流程。
 * @returns {Promise<Object|null>} 新变为可领取的任务；已完成/已领取/非金币则 null
 */
export async function runTopUpRewardTaskFlow (uid, product, position) {
  if (!uid || !isCoinTopUpProduct(product)) return null

  try {
    return await tryAutoCompleteTopUpTask(uid, product, position)
  } catch (error) {
    if (typeof logger !== 'undefined') {
      logger.debug('[runTopUpRewardTaskFlow] error', error)
    }
    return null
  }
}

/** Home onMounted 引导弹窗类型（PromptDialog） */
export const HOME_ENV_PROMPT = {
  /** WebView + task_browser 未完成：引导复制链接到系统浏览器领取 */
  WEBVIEW_BROWSER: 'webview_browser',
  /** WebView + task_browser 已完成：仍引导去浏览器，提示可获得更多奖励 */
  WEBVIEW_BROWSER_MORE_REWARDS: 'webview_browser_more_rewards',
  /** Browser + task_add_homescreen 未完成：引导添加到桌面 */
  ADD_HOMESCREEN: 'add_homescreen',
  /** Browser + task_homescreen 未完成：引导从桌面打开 */
  OPEN_HOMESCREEN: 'open_homescreen'
}

/**
 * Home 进入时按运行环境处理三类 Web 专属任务：
 * - task_browser（daily）：系统浏览器打开
 * - task_add_homescreen（new_user）：首次添加到桌面
 * - task_homescreen（daily）：从桌面/PWA 打开
 *
 * 返回 celebrate（claim）或 prompt；WebView 内不 execute，仅引导。
 */
export async function resolveHomeEnvOnMount (uid) {
  if (!ENABLE_REWARD_TASKS) return null

  const env = getClientEnv()
  logger.debug('[resolveHomeEnvOnMount] env:', env)

  try {
    const { tasks } = await getRewardTasks(uid)
    const statusOf = (taskId) => findTaskById(tasks, taskId)?.status

    // ── WebView（微信/FB 等内置浏览器）────────────────────────────
    if (env.runtime === Runtime.WEBVIEW) {
      const browserStatus = statusOf(REWARD_TASK_ID.BROWSER)

      // task_browser 未完成：引导复制链接，去系统浏览器完成每日任务
      if (browserStatus === TASK_STATUS.UNFINISHED) {
        return { type: 'prompt', prompt: HOME_ENV_PROMPT.WEBVIEW_BROWSER }
      }

      // task_browser 已完成（可领取/已领取）：弹窗逻辑相同（复制链接），文案提示还有更多奖励
      return { type: 'prompt', prompt: HOME_ENV_PROMPT.WEBVIEW_BROWSER_MORE_REWARDS }
    }

    // ── 系统浏览器（非 WebView、非 PWA）────────────────────────────
    if (env.runtime === Runtime.BROWSER) {
      // 1) 自动完成 task_browser → 庆祝弹窗
      if (statusOf(REWARD_TASK_ID.BROWSER) === TASK_STATUS.UNFINISHED) {
        const task = await tryExecuteTaskIfUnfinished(uid, tasks, REWARD_TASK_ID.BROWSER, 'home')
        return task ? { type: 'claim', task } : null
      }

      // 2) 引导添加到桌面（task_add_homescreen，new_user，仅 Prompt 不 execute）
      if (statusOf(REWARD_TASK_ID.ADD_HOMESCREEN) === TASK_STATUS.UNFINISHED) {
        return { type: 'prompt', prompt: HOME_ENV_PROMPT.ADD_HOMESCREEN }
      }

      // 3) 引导从桌面打开（task_homescreen，daily，仅 Prompt）
      if (statusOf(REWARD_TASK_ID.HOMESCREEN) === TASK_STATUS.UNFINISHED) {
        return { type: 'prompt', prompt: HOME_ENV_PROMPT.OPEN_HOMESCREEN }
      }

      return null
    }

    // ── PWA / 从桌面图标打开 ───────────────────────────────────────
    if (env.runtime === Runtime.PWA) {
      // 自动完成 new_user 添加桌面 + daily 从桌面打开；庆祝弹窗优先展示 add
      const addResult = await tryExecuteTaskIfUnfinished(
        uid,
        tasks,
        REWARD_TASK_ID.ADD_HOMESCREEN,
        'home'
      )
      const homeResult = await tryExecuteTaskIfUnfinished(
        uid,
        tasks,
        REWARD_TASK_ID.HOMESCREEN,
        'home'
      )
      if (addResult?.task_id) {
        trackAddHomescreenPwaOpen({ taskId: addResult.task_id })
      }
      if (homeResult?.task_id) {
        trackAddHomescreenPwaOpen({ taskId: homeResult.task_id })
      }
      const task = addResult || homeResult
      return task ? { type: 'claim', task } : null
    }

    return null
  } catch {
    return null
  }
}

/**
 * 按运行环境自动完成任务（仅返回庆祝弹窗任务，不含引导弹窗）：
 * - browser → task_browser
 * - pwa → task_add_homescreen，再 task_homescreen
 * @returns {Promise<Object|null>} 本次新变为可领取的任务；无则 null
 */
export async function tryAutoCompleteEnvRewardTask (uid) {
  if (!uid) return null
  const result = await resolveHomeEnvOnMount(uid)
  return result?.type === 'claim' ? result.task : null
}

/** 分享成功后尝试完成 task_share（仅 status=0 时执行） */
export async function tryAutoCompleteShareTask (uid, position) {
  if (!uid) return null

  try {
    const { tasks } = await getRewardTasks(uid)
    return tryExecuteTaskIfUnfinished(uid, tasks, REWARD_TASK_ID.SHARE, position)
  } catch {
    return null
  }
}

/** 分享弹窗确认方式：Video 跳转领取 / Play 提示后关闭（分享、观看等任务共用） */
export const SHARE_CLAIM_MODE = {
  REDIRECT: 'redirect',
  HINT: 'hint'
}

/** 观看任务：单段真实播放小于 5 秒视为误播放，不上报。 */
export const WATCH_REWARD_MIN_SECONDS = 5

/**
 * 分享成功后执行任务完成流程。
 * @returns {Promise<Object|null>} 新变为可领取的任务；无需处理则 null
 */
export async function runShareRewardTaskFlow (uid, position) {
  if (!uid) return null

  try {
    return await tryAutoCompleteShareTask(uid, position)
  } catch (error) {
    if (typeof logger !== 'undefined') {
      logger.debug('[runShareRewardTaskFlow] error', error)
    }
    return null
  }
}

/** 首次收藏成功后尝试完成 task_first_favorite（仅 status=0 时执行） */
export async function tryAutoCompleteFirstFavoriteTask (uid, position) {
  if (!uid) return null

  try {
    const { tasks } = await getRewardTasks(uid)
    return tryExecuteTaskIfUnfinished(uid, tasks, REWARD_TASK_ID.FIRST_FAVORITE, position)
  } catch {
    return null
  }
}

/**
 * 收藏接口成功后执行任务完成流程。
 * @returns {Promise<Object|null>} 新变为可领取的任务；无需处理则 null
 */
export async function runFirstFavoriteRewardTaskFlow (uid, position) {
  if (!uid) return null

  try {
    return await tryAutoCompleteFirstFavoriteTask(uid, position)
  } catch (error) {
    if (typeof logger !== 'undefined') {
      logger.debug('[runFirstFavoriteRewardTaskFlow] error', error)
    }
    return null
  }
}

/** 反馈流程成功后尝试完成 task_feedback（仅 status=0 时执行） */
export async function tryAutoCompleteFeedbackTask (uid) {
  if (!uid) return null

  try {
    const { tasks } = await getRewardTasks(uid)
    return tryExecuteTaskIfUnfinished(uid, tasks, REWARD_TASK_ID.FEEDBACK, 'mine')
  } catch {
    return null
  }
}

/**
 * Feedback 页成功调起邮件客户端后执行任务完成流程。
 * @returns {Promise<Object|null>} 新变为可领取的任务；无需处理则 null
 */
export async function runFeedbackRewardTaskFlow (uid) {
  if (!uid) return null

  try {
    return await tryAutoCompleteFeedbackTask(uid)
  } catch (error) {
    if (typeof logger !== 'undefined') {
      logger.debug('[runFeedbackRewardTaskFlow] error', error)
    }
    return null
  }
}

/**
 * 分享任务庆祝弹窗确认后的行为。
 * @returns {{ type: 'redirect', route: Object } | { type: 'hint', messageKey: string }}
 */
export function getShareClaimConfirmAction (task, claimMode = SHARE_CLAIM_MODE.REDIRECT) {
  const taskId = task?.task_id || REWARD_TASK_ID.SHARE

  if (claimMode === SHARE_CLAIM_MODE.HINT) {
    return {
      type: 'hint',
      messageKey: 'reward.share_claim_hint'
    }
  }

  return {
    type: 'redirect',
    route: getRewardClaimRedirectRoute(task, taskId)
  }
}

/**
 * 有效观看片段后按秒推进 task_watch_20min 进度；仅在任务变为可领取时返回 task。
 * @param {number} watchSeconds 本段真实播放秒数
 */
export async function runWatchRewardTaskFlow (uid, watchSeconds, position) {
  const progressDelta = Math.floor(Number(watchSeconds) || 0)
  if (!uid || progressDelta < WATCH_REWARD_MIN_SECONDS) return null

  try {
    const { tasks } = await getRewardTasks(uid)
    const task = (tasks || []).find((item) => item?.task_id === REWARD_TASK_ID.WATCH_20MIN)
    if (!task || task.status !== TASK_STATUS.UNFINISHED) return null

    const updated = await executeTask(uid, REWARD_TASK_ID.WATCH_20MIN, progressDelta, position)
    if (!updated || updated.status !== TASK_STATUS.CLAIMABLE) return null
    return updated
  } catch (error) {
    if (typeof logger !== 'undefined') {
      logger.debug('[runWatchRewardTaskFlow] error', error)
    }
    return null
  }
}

/** Home Banner 点击 Facebook 时尝试完成 task_fb_page（仅 status=0 时执行） */
export async function tryAutoCompleteFbPageTask (uid, position) {
  if (!uid) return null

  try {
    const { tasks } = await getRewardTasks(uid)
    return tryExecuteTaskIfUnfinished(uid, tasks, REWARD_TASK_ID.FB_PAGE, position)
  } catch {
    return null
  }
}

/**
 * Reward 页 task_fb_page 未完成：打开 Facebook 主页并 execute 完成任务。
 * @returns {Promise<Object|null>} 新变为可领取的任务；已完成则 null
 */
export async function runRewardFbPageTaskAction (uid) {
  const opened = openFacebookPage()

  if (!opened || !uid) return null

  try {
    return await tryAutoCompleteFbPageTask(uid, 'reward')
  } catch (error) {
    if (typeof logger !== 'undefined') {
      logger.debug('[runRewardFbPageTaskAction] error', error)
    }
    return null
  }
}

/** Home Banner 跳转 Facebook 前执行任务完成流程。
 * @returns {Promise<Object|null>} 新变为可领取的任务；已完成或无需处理则 null
 */
export async function runFbPageRewardTaskFlow (uid) {
  const opened = openFacebookPage()

  if (!opened || !uid) return null

  try {
    return await tryAutoCompleteFbPageTask(uid, 'home')
  } catch (error) {
    if (typeof logger !== 'undefined') {
      logger.debug('[runFbPageRewardTaskFlow] error', error)
    }
    return null
  }
}

/** 登录完成后尝试完成 task_login（仅 status=0 时执行） */
export async function tryAutoCompleteLoginTask (uid) {
  if (!uid) return null

  try {
    const { tasks } = await getRewardTasks(uid)
    return tryExecuteTaskIfUnfinished(uid, tasks, REWARD_TASK_ID.LOGIN, 'login')
  } catch {
    return null
  }
}

/**
 * 登录成功后执行任务完成流程。
 * @returns {Promise<Object|null>}
 */
export async function runLoginRewardTaskFlow (uid) {
  if (!uid) return null

  try {
    return await tryAutoCompleteLoginTask(uid)
  } catch (error) {
    if (typeof logger !== 'undefined') {
      logger.debug('[runLoginRewardTaskFlow] error', error)
    }
    return null
  }
}

/** 庆祝弹窗确认后跳转 Reward 领取 */
export function getRewardClaimRedirectRoute (task, fallbackTaskId) {
  const taskId = task?.task_id || fallbackTaskId
  return {
    path: '/reward',
    query: { claimTask: taskId }
  }
}

/** 领取 task_browser 奖励 */
export async function claimBrowserTask (uid) {
  return claimRewardTask(uid, REWARD_TASK_ID.BROWSER)
}
